import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Shield, Zap, Save, Loader2, Info, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { numOrDefault } from '@/lib/invoiceHelpers';

// Split out of CompanySettingsTab.jsx -- these settings (insurance/ELD
// deductions, carrier take %) are about how the CARRIER pays OTHER
// drivers, so they belong in Accounting next to Driver Pay Mgt/Payroll,
// not in general company profile settings. Also Owner-Op-only carriers
// never see this tab (AdminPanel.jsx excludes 'pay-settings' from their
// Accounting subs) -- an O/O has no other driver to deduct against.
const SETTINGS_KEYS = {
  INSURANCE_MONTHLY: 'insurance_monthly_cost',
  ELD_WEEKLY: 'eld_weekly_cost',
  CARRIER_TAKE: 'carrier_take_percent',
  DISPATCH_FEE: 'dispatch_fee_percent',
};

const sameShape = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function PayDeductionSettingsTab() {
  const [insuranceMonthly, setInsuranceMonthly] = useState('');
  const [eldWeekly, setEldWeekly] = useState('');
  const [carrierTake, setCarrierTake] = useState('15');
  const [dispatchFee, setDispatchFee] = useState('3');
  const [initialOps, setInitialOps] = useState({ insuranceMonthly: '', eldWeekly: '', carrierTake: '15', dispatchFee: '3' });
  const [settingIds, setSettingIds] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const settings = await client.entities.CompanySettings.list();
    const ids = {};
    let nextInsurance = '';
    let nextEld = '';
    let nextCarrier = '15';
    let nextDispatch = '3';
    settings.forEach(s => {
      ids[s.key] = s.id;
      if (s.key === SETTINGS_KEYS.INSURANCE_MONTHLY) nextInsurance = s.value || '';
      if (s.key === SETTINGS_KEYS.ELD_WEEKLY) nextEld = s.value || '';
      if (s.key === SETTINGS_KEYS.CARRIER_TAKE) nextCarrier = s.value || '15';
      if (s.key === SETTINGS_KEYS.DISPATCH_FEE) nextDispatch = s.value || '3';
    });
    setSettingIds(ids);
    setInsuranceMonthly(nextInsurance);
    setEldWeekly(nextEld);
    setCarrierTake(nextCarrier);
    setDispatchFee(nextDispatch);
    setInitialOps({ insuranceMonthly: nextInsurance, eldWeekly: nextEld, carrierTake: nextCarrier, dispatchFee: nextDispatch });
    setLoading(false);
  };

  const upsertKey = async (key, value, label) => {
    const existing = settingIds[key];
    if (existing) {
      await client.entities.CompanySettings.update(existing, { key, value: String(value), label });
    } else {
      const created = await client.entities.CompanySettings.create({ key, value: String(value), label });
      setSettingIds(prev => ({ ...prev, [key]: created.id }));
    }
  };

  const upsert = async (key, value, label) => {
    await upsertKey(key, value, label);
    // Keep insurance_deduction in sync (weekly breakdown)
    const insuranceWeekly = key === SETTINGS_KEYS.INSURANCE_MONTHLY
      ? (parseFloat(value) || 0) / 4.33
      : (parseFloat(insuranceMonthly) || 0) / 4.33;
    const insuranceId = settingIds['insurance_deduction'];
    if (insuranceId) {
      await client.entities.CompanySettings.update(insuranceId, {
        key: 'insurance_deduction',
        value: String(insuranceWeekly.toFixed(2)),
        label: 'Insurance Weekly Deduction (auto-calculated)',
      });
    } else {
      const created = await client.entities.CompanySettings.create({
        key: 'insurance_deduction',
        value: String(insuranceWeekly.toFixed(2)),
        label: 'Insurance Weekly Deduction (auto-calculated)',
      });
      setSettingIds(prev => ({ ...prev, insurance_deduction: created.id }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsert(SETTINGS_KEYS.INSURANCE_MONTHLY, parseFloat(insuranceMonthly) || 0, 'Monthly Insurance Cost'),
        upsert(SETTINGS_KEYS.ELD_WEEKLY, parseFloat(eldWeekly) || 0, 'ELD Weekly Cost'),
        // `parseFloat(carrierTake) || 15` silently overwrote a deliberate
        // "0" typed into this field back to 15% on every save.
        upsertKey(SETTINGS_KEYS.CARRIER_TAKE, numOrDefault(carrierTake, 15), 'Carrier Take Percentage'),
        upsertKey(SETTINGS_KEYS.DISPATCH_FEE, parseFloat(dispatchFee) || 0, 'Dispatch Fee Percentage'),
      ]);
      setInitialOps({ insuranceMonthly, eldWeekly, carrierTake, dispatchFee });
      toast.success('Pay & deduction settings saved.');
    } catch (e) {
      toast.error(e?.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const insuranceWeekly = (parseFloat(insuranceMonthly) || 0) / 4.33;
  const insurancePerLoad = insuranceWeekly;
  const totalWeeklyFixed = insuranceWeekly + (parseFloat(eldWeekly) || 0);
  const isOpsDirty = !sameShape({ insuranceMonthly, eldWeekly, carrierTake, dispatchFee }, initialOps);

  if (loading) {
    return (
      <div className="glass-card p-12 flex justify-center">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">

      {/* Pay Period Info */}
      <div className="glass-card p-5 flex items-start gap-3">
        <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Pay Period:</span> Monday → Monday &nbsp;·&nbsp;
          <span className="font-semibold text-foreground">Paid on:</span> Friday &nbsp;·&nbsp;
          O/O start each week <span className="font-semibold text-red-400">in the negative</span> — insurance & ELD are deducted at the top of every pay period before load earnings are applied.
        </div>
      </div>

      {/* Insurance */}
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Shield className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <h3 className="font-space font-semibold text-foreground">Insurance Cost</h3>
            <p className="text-xs text-muted-foreground">Enter the monthly premium — the weekly amount is auto-calculated and applied per pay period</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-2">Monthly Insurance Premium ($)</label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={insuranceMonthly}
              onChange={e => setInsuranceMonthly(e.target.value)}
              placeholder="0.00"
              className="w-full pl-7 pr-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        {parseFloat(insuranceMonthly) > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-muted-foreground mb-1">Calculated Weekly Amount</p>
              <p className="text-lg font-bold text-blue-300">${insuranceWeekly.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">${parseFloat(insuranceMonthly).toFixed(2)} ÷ 4.33</p>
            </div>
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-muted-foreground mb-1">Deducted Each Week</p>
              <p className="text-lg font-bold text-red-400">−${insurancePerLoad.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">O/O starts week in negative</p>
            </div>
          </div>
        )}
      </div>

      {/* ELD Cost */}
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Zap className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h3 className="font-space font-semibold text-foreground">ELD Cost</h3>
            <p className="text-xs text-muted-foreground">Weekly ELD subscription cost deducted from each pay period</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-2">ELD Weekly Cost ($)</label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={eldWeekly}
              onChange={e => setEldWeekly(e.target.value)}
              placeholder="0.00"
              className="w-full pl-7 pr-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        {parseFloat(eldWeekly) > 0 && (
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-muted-foreground mb-1">Weekly ELD Deduction</p>
            <p className="text-lg font-bold text-amber-300">−${(parseFloat(eldWeekly) || 0).toFixed(2)}</p>
          </div>
        )}
      </div>

      {/* Carrier Take % */}
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Percent className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-space font-semibold text-foreground">Carrier Take %</h3>
            <p className="text-xs text-muted-foreground">Percentage deducted from every load's gross revenue</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-2">Carrier Take (%)</label>
          <div className="relative max-w-xs">
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={carrierTake}
              onChange={e => setCarrierTake(e.target.value)}
              placeholder="15"
              className="w-full pr-8 pl-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
          </div>
        </div>

        <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
          <p className="text-xs text-muted-foreground mb-1">Applied per Load</p>
          <p className="text-lg font-bold text-primary">{numOrDefault(carrierTake, 15)}% of gross revenue</p>
          <p className="text-xs text-muted-foreground">Deducted from every load's gross revenue.</p>
        </div>
      </div>

      {/* Dispatch Fee % */}
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Percent className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <h3 className="font-space font-semibold text-foreground">Dispatch Fee %</h3>
            <p className="text-xs text-muted-foreground">Percentage deducted from Owner Operators who use the carrier's dispatch service -- has no effect on drivers with dispatch service off, or on company drivers</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-2">Dispatch Fee (%)</label>
          <div className="relative max-w-xs">
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={dispatchFee}
              onChange={e => setDispatchFee(e.target.value)}
              placeholder="3"
              className="w-full pr-8 pl-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
          </div>
        </div>

        <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
          <p className="text-xs text-muted-foreground mb-1">Applied per Load</p>
          <p className="text-lg font-bold text-purple-300">{parseFloat(dispatchFee) || 0}% of gross revenue</p>
          <p className="text-xs text-muted-foreground">Only charged to Owner Operators with "Dispatch Service" turned on (set per driver in their profile).</p>
        </div>
      </div>

      {/* Total weekly fixed costs summary */}
      {totalWeeklyFixed > 0 && (
        <div className="glass-card p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Total Weekly Fixed Costs (Insurance + ELD)</span>
            <span className="font-bold text-red-400 text-lg">−${totalWeeklyFixed.toFixed(2)}</span>
          </div>
          <p className="text-xs text-muted-foreground">Driver-specific truck & trailer payments are configured individually under each driver's Settings tab.</p>
        </div>
      )}

      <Button
        onClick={handleSave}
        disabled={!isOpsDirty || saving}
        className={
          isOpsDirty
            ? 'bg-prohaul-red hover:bg-prohaul-red/90 text-white'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }
      >
        {saving
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          : <><Save className="w-4 h-4 mr-2" /> Save Settings</>}
      </Button>
    </div>
  );
}
